import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CATEGORY_LABELS, CATEGORY_ORDER, downloadDocument } from "../lib/documents";
import { CategoryIcon } from "../lib/fileIcons";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Archive, Loader2, Search, Download, ChevronLeft, ChevronRight, RotateCcw, ArrowRight, PenLine, CheckCircle2, Pencil, Eye,
    ArrowUp, ArrowDown, ChevronsUpDown, Mail,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { DatePicker } from "../components/DatePicker";
import { DocumentPreviewDialog } from "../components/DocumentPreviewDialog";

const ALL = "__all__";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
};

export default function DocumentsArchivePage() {
    const [q, setQ] = useState("");
    const [employeeId, setEmployeeId] = useState(ALL);
    const [category, setCategory] = useState(ALL);
    const [ack, setAck] = useState(ALL);
    const [origin, setOrigin] = useState(ALL);
    const [expiry, setExpiry] = useState(ALL);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("");
    const [sortDir, setSortDir] = useState("asc");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [editDoc, setEditDoc] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [confirmNotify, setConfirmNotify] = useState(false);
    const [notifyBusy, setNotifyBusy] = useState(false);

    const handleArchiveNotify = async () => {
        setNotifyBusy(true);
        try {
            const p = { ...params };
            delete p.page; delete p.page_size; delete p.sort_by; delete p.sort_dir;
            const { data: res } = await api.post("/documents/archive/notify", null, { params: p });
            toast.success(`Notifiche email in invio per ${res.queued} documenti`);
            setConfirmNotify(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio notifiche");
        } finally {
            setNotifyBusy(false);
        }
    };
    const debounce = useRef(null);

    useEffect(() => {
        api.get("/employees").then((r) => {
            const list = [...r.data].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`));
            setEmployees(list);
        }).catch(() => {});
    }, []);

    const params = useMemo(() => {
        const p = { page, page_size: 50 };
        if (q.trim()) p.q = q.trim();
        if (employeeId !== ALL) p.employee_id = employeeId;
        if (category !== ALL) p.category = category;
        if (ack !== ALL) p.ack = ack;
        if (origin !== ALL) p.origin = origin;
        if (expiry !== ALL) p.expiry = expiry;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        if (sortBy) { p.sort_by = sortBy; p.sort_dir = sortDir; }
        return p;
    }, [q, employeeId, category, ack, origin, expiry, dateFrom, dateTo, page, sortBy, sortDir]);

    useEffect(() => {
        clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data: res } = await api.get("/documents/archive/search", { params });
                setData(res);
            } catch (e) {
                toast.error("Errore nel caricamento dell'archivio");
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => clearTimeout(debounce.current);
    }, [params]);

    const resetFilters = () => {
        setQ(""); setEmployeeId(ALL); setCategory(ALL); setAck(ALL);
        setOrigin(ALL); setExpiry(ALL); setDateFrom(""); setDateTo(""); setPage(1);
    };

    const changeFilter = (setter) => (v) => { setter(v); setPage(1); };

    // 3-state column sorting: asc -> desc -> default (uploaded_at desc)
    const requestSort = (field) => {
        setPage(1);
        if (sortBy !== field) {
            setSortBy(field);
            setSortDir("asc");
        } else if (sortDir === "asc") {
            setSortDir("desc");
        } else {
            setSortBy("");
            setSortDir("asc");
        }
    };

    const SortableTh = ({ label, field, right }) => (
        <th
            className={`cursor-pointer select-none px-3 py-2.5 transition-colors hover:text-slate-700 ${right ? "text-right" : ""}`}
            onClick={() => requestSort(field)}
            title={`Ordina per ${label}`}
            data-testid={`archive-sort-${field}`}
        >
            <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
                {label}
                {sortBy === field ? (
                    sortDir === "asc"
                        ? <ArrowUp className="h-3 w-3 text-[#376DB3]" />
                        : <ArrowDown className="h-3 w-3 text-[#376DB3]" />
                ) : (
                    <ChevronsUpDown className="h-3 w-3 text-slate-300" />
                )}
            </span>
        </th>
    );

    const handleDownload = async (doc) => {
        setDownloadingId(doc.id);
        try {
            await downloadDocument(doc.id, doc.filename);
        } catch (e) {
            toast.error("Errore nel download");
        } finally {
            setDownloadingId(null);
        }
    };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    const handleSaved = (updated) => {
        setData((prev) => prev ? {
            ...prev,
            items: prev.items.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)),
        } : prev);
        setEditDoc(null);
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="documents-archive-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                    <Archive className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">Archivio documenti</h1>
                    <p className="text-sm text-slate-500">
                        Tutti i documenti caricati{data ? <> — <b>{data.total.toLocaleString("it-IT")}</b> risultati</> : ""}
                    </p>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="relative min-w-[220px] flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Nome file o titolo…"
                        className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-700"
                        data-testid="archive-search-input"
                    />
                </div>
                <Select value={employeeId} onValueChange={changeFilter(setEmployeeId)}>
                    <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="archive-filter-employee">
                        <SelectValue placeholder="Dipendente" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value={ALL}>Tutti i dipendenti</SelectItem>
                        {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.cognome} {e.nome}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={category} onValueChange={changeFilter(setCategory)}>
                    <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="archive-filter-category">
                        <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Tutte le categorie</SelectItem>
                        {CATEGORY_ORDER.map((c) => (
                            <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={ack} onValueChange={changeFilter(setAck)}>
                    <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="archive-filter-ack">
                        <SelectValue placeholder="Presa visione" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Presa visione: tutte</SelectItem>
                        <SelectItem value="pending">Da firmare</SelectItem>
                        <SelectItem value="done">Firmati</SelectItem>
                        <SelectItem value="none">Non richiesta</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={origin} onValueChange={changeFilter(setOrigin)}>
                    <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="archive-filter-origin">
                        <SelectValue placeholder="Origine" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Origine: tutte</SelectItem>
                        <SelectItem value="zip">Import ZIP</SelectItem>
                        <SelectItem value="generator">Generatore contratti</SelectItem>
                        <SelectItem value="manual">Caricamento manuale</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={expiry} onValueChange={changeFilter(setExpiry)}>
                    <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="archive-filter-expiry">
                        <SelectValue placeholder="Scadenza" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Scadenza: tutte</SelectItem>
                        <SelectItem value="with">Con scadenza</SelectItem>
                        <SelectItem value="expiring60">In scadenza (60 gg)</SelectItem>
                        <SelectItem value="expired">Scaduti</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs text-slate-400">Dal</span>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" title="Caricati dal" data-testid="archive-date-from" />
                    <span className="shrink-0 text-xs text-slate-400">al</span>
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" title="Caricati fino al" data-testid="archive-date-to" />
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={resetFilters} title="Azzera filtri" data-testid="archive-reset-filters">
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-9 shrink-0 gap-1.5 text-xs"
                        onClick={() => setConfirmNotify(true)}
                        disabled={!data || !data.total}
                        title="Invia ai dipendenti l'email 'nuovo documento disponibile' per tutti i documenti filtrati"
                        data-testid="archive-notify-btn"
                    >
                        <Mail className="h-3.5 w-3.5 text-[#376DB3]" />
                        Invia notifiche email
                    </Button>
                </div>
            </div>

            <Dialog open={confirmNotify} onOpenChange={setConfirmNotify}>
                <DialogContent className="max-w-md" data-testid="archive-notify-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Invia notifiche email</DialogTitle>
                        <DialogDescription>
                            Verrà inviata l'email "nuovo documento disponibile" ai dipendenti per{" "}
                            <b>{data?.total || 0} documenti</b> corrispondenti ai filtri attuali (solo dipendenti
                            con email e accesso attivo). Continuare?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setConfirmNotify(false)} data-testid="archive-notify-cancel">
                            Annulla
                        </Button>
                        <Button
                            onClick={handleArchiveNotify}
                            disabled={notifyBusy}
                            className="gap-1.5 bg-[#376DB3] text-white hover:bg-[#2c5a96]"
                            data-testid="archive-notify-confirm"
                        >
                            {notifyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Invia notifiche
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                </div>
            ) : !data || data.items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="archive-empty">
                    Nessun documento trovato con i filtri selezionati.
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <SortableTh label="Documento" field="filename" />
                                    <SortableTh label="Dipendente" field="employee" />
                                    <SortableTh label="Categoria" field="category" />
                                    <SortableTh label="Periodo/Scadenza" field="period" />
                                    <SortableTh label="Caricato" field="uploaded_at" />
                                    <SortableTh label="Presa visione" field="requires_acknowledgment" />
                                    <th className="px-3 py-2.5 text-right">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((d) => (
                                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`archive-row-${d.id}`}>
                                        <td className="max-w-md px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <CategoryIcon category={d.category} />
                                                <div className="min-w-0">
                                                    <div className="truncate font-semibold text-slate-700">{d.title || d.filename}</div>
                                                    <div className="truncate text-[11px] text-slate-400">{d.filename}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {d.employee_id ? (
                                                <Link to={`/dipendenti/${d.employee_id}`} className="flex items-center gap-1 text-slate-700 hover:text-[#376DB3] hover:underline">
                                                    {d.employee_name}
                                                    <ArrowRight className="h-3 w-3 text-slate-300" />
                                                </Link>
                                            ) : "—"}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                                {CATEGORY_LABELS[d.category] || d.category}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                                            {d.period || (d.expires_at ? `scad. ${fmtDate(d.expires_at)}` : "—")}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-500">{fmtDate(d.uploaded_at)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs">
                                            {d.acknowledged ? (
                                                <span className="flex items-center gap-1 font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Firmato</span>
                                            ) : d.requires_acknowledgment ? (
                                                <span className="flex items-center gap-1 font-semibold text-amber-700"><PenLine className="h-3.5 w-3.5" /> Da firmare</span>
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-nowrap items-center justify-end gap-0.5">
                                                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setPreviewDoc(d)}
                                                    title="Visualizza" data-testid={`archive-preview-${d.id}`}>
                                                    <Eye className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setEditDoc(d)}
                                                    title="Modifica titolo, periodo e scadenza" data-testid={`archive-edit-${d.id}`}>
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => handleDownload(d)}
                                                    disabled={downloadingId === d.id} title="Scarica" data-testid={`archive-download-${d.id}`}>
                                                    {downloadingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span data-testid="archive-pagination-info">
                            {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} di {data.total.toLocaleString("it-IT")}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="archive-prev-page">
                                <ChevronLeft className="h-4 w-4" /> Prec.
                            </Button>
                            <span className="font-mono-numbers">pag. {data.page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="archive-next-page">
                                Succ. <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <ArchiveEditDialog doc={editDoc} onClose={() => setEditDoc(null)} onSaved={handleSaved} />
            <DocumentPreviewDialog open={!!previewDoc} document={previewDoc} onClose={() => setPreviewDoc(null)} />
        </div>
    );
}

function ArchiveEditDialog({ doc, onClose, onSaved }) {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("altro");
    const [period, setPeriod] = useState("");
    const [expiresAt, setExpiresAt] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (doc) {
            setTitle(doc.title || "");
            setCategory(doc.category || "altro");
            setPeriod(doc.period || "");
            setExpiresAt(doc.expires_at || "");
        }
    }, [doc]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/documents/${doc.id}`, {
                title: title.trim() || null,
                category,
                period: period.trim() || null,
                expires_at: expiresAt || null,
            });
            toast.success("Documento aggiornato");
            onSaved(data);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error((typeof detail === "string" && detail) || "Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto overflow-x-hidden"
                data-testid="archive-edit-dialog"
            >
                <DialogHeader className="min-w-0">
                    <DialogTitle className="font-display text-slate-800">Modifica documento</DialogTitle>
                    <DialogDescription className="break-words">{doc?.filename}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Titolo</Label>
                        <Input
                            className="mt-1"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Titolo del documento"
                            data-testid="archive-edit-title"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Categoria</Label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
                            data-testid="archive-edit-category"
                        >
                            {CATEGORY_ORDER.map((c) => (
                                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Periodo / data documento (opzionale)</Label>
                        <Input
                            className="mt-1"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            placeholder="Es. 2026-06 per giugno 2026"
                            data-testid="archive-edit-period"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Scadenza (opzionale)</Label>
                        <div className="mt-1">
                            <DatePicker
                                value={expiresAt}
                                onChange={setExpiresAt}
                                placeholder="Nessuna scadenza"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose} data-testid="archive-edit-cancel">Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="archive-edit-save">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
